"use client";

import { useMemo } from "react";
import { useQuery } from "convex/react";
import type { FunctionReturnType } from "convex/server";
import { cn } from "@/lib/utils";
import { ALL_REGIONS, type Region } from "@shared/formats/state";
import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import {
  InlineCombobox,
  type InlineComboboxOption,
} from "@/components/ui/inline-combobox";

interface StateOption extends InlineComboboxOption {
  stateId?: Id<"states">;
}

export interface StateAutocompleteProps {
  value: string;
  onChange: (code: string, stateId?: Id<"states">) => void;
  compact?: boolean;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
  id?: string;
  error?: boolean;
  "aria-invalid"?: boolean;
  "aria-describedby"?: string;
  "aria-required"?: boolean;
  includeCanada?: boolean;
}

type DatabaseStates = FunctionReturnType<typeof api.states.queries.list>;

function regionOption(region: Region): StateOption {
  return {
    value: region.code,
    label: region.name,
    description: region.code,
    keywords: [region.code, region.name, region.country],
  };
}

function databaseStateOptions(
  dbStates: DatabaseStates,
  includeCanada: boolean,
): StateOption[] {
  const seenCodes = new Set<string>();
  return dbStates
    .filter((state) => {
      const region = ALL_REGIONS.find((candidate) => candidate.code === state.code);
      if (!includeCanada && region?.country === "CA") return false;
      if (seenCodes.has(state.code)) return false;
      seenCodes.add(state.code);
      return true;
    })
    .map((state) => ({
      value: state.code,
      label: state.name,
      description: state.code,
      keywords: [state.code, state.name],
      stateId: state._id,
    }));
}

function buildStateOptions(
  dbStates: DatabaseStates | undefined,
  includeCanada: boolean,
): StateOption[] {
  const regions = ALL_REGIONS.filter(
    (region) => includeCanada || region.country === "US",
  );
  if (!dbStates || dbStates.length === 0) return regions.map(regionOption);
  const databaseOptions = databaseStateOptions(dbStates, includeCanada);
  const databaseCodes = new Set(databaseOptions.map((option) => option.value));
  return databaseOptions.concat(
    regions
      .filter((region) => !databaseCodes.has(region.code))
      .map(regionOption),
  );
}

export function StateAutocomplete({
  value,
  onChange,
  compact = false,
  className,
  placeholder = "Select state...",
  disabled = false,
  error = false,
  id,
  "aria-invalid": ariaInvalid,
  "aria-describedby": ariaDescribedby,
  "aria-required": ariaRequired,
  includeCanada = false,
}: StateAutocompleteProps) {
  const dbStates = useQuery(api.states.queries.list, {});

  const options: StateOption[] = useMemo(
    () => buildStateOptions(dbStates, includeCanada),
    [dbStates, includeCanada],
  );

  const handleValueChange = (next: string | null) => {
    if (!next) {
      onChange("");
      return;
    }
    const picked = options.find((opt) => opt.value === next);
    onChange(next, picked?.stateId);
  };

  return (
    <InlineCombobox
      options={options}
      value={value || null}
      onSelect={handleValueChange}
      placeholder={placeholder}
      searchPlaceholder={
        includeCanada ? "Search states or provinces..." : "Search states..."
      }
      emptyText={includeCanada ? "No region matches." : "No state matches."}
      disabled={disabled}
      id={id}
      ariaInvalid={ariaInvalid ?? error}
      ariaDescribedby={ariaDescribedby}
      ariaRequired={ariaRequired}
      className={cn(error && "border-destructive", className)} size={compact ? "compact" : "default"}
    />
  );
}
